import React, { memo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { useIntl } from 'react-intl';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { Grid, Avatar } from '@material-ui/core';
import styles from './replaystreamingRoom.styles';
import { Button } from '../../components/Button';
import { Typography } from '../../components/Typography';
import { TextField } from '../../components/TextField';
import { Card } from '../../components/Card';
import { validateEmail } from '../../helper/validation';
import { Clock } from '../../components/Clock';
// import { LoadingLayout } from '../../components/layout';
import { useAppDispatch } from '../../app/hooks';
import { actions } from './replaystreamingRoom.slice';
import WaitingTime from './component/WaitingTime';

interface IReplaystreamingRoomView extends WithStyles<typeof styles> {
  roomName: string;
  viewingUrl: string;
  roomTitle: string;
  user: any;
  startTime: string;
  endTime: string;
  backToHomeButtonOnClick: () => void;
}

const ReplaystreamingRoomView: React.FC<IReplaystreamingRoomView> = ({
  classes,
  roomName = '',
  viewingUrl = '',
  roomTitle = '',
  user = null,
  startTime = '',
  endTime = '',
  backToHomeButtonOnClick,
}: IReplaystreamingRoomView) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLobby, setIsLobby] = useState<boolean>(true);
  const [conferenceLeft, setConferenceLeft] = useState<boolean>(false);
  const [name, setName] = useState<any>({ value: '', error: false });
  const [email, setEmail] = useState<any>({ value: '', error: false });
  const intl = useIntl();
  const [showWaiting, setShowWaiting] = useState<boolean>(false);
  const [waitingTime, setWaitingTime] = useState<string>('00:00');
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (user?.email && user?.profile?.name) {
      setName({ value: user?.profile?.name, error: false });
      setEmail({ value: user?.email, error: false });
      // when authenticated user arrives, set skip join page to true
      // setSkipJoinPage(true);
    }
  }, [user]);

  useEffect(() => {
    setIsLobby(false);
    setConferenceLeft(false);
    // setIsLoading(true);
    dispatch(actions.resetSuccess());
  }, []);

  useEffect(() => {
    const startCountdown = () => {
      const now = Math.floor(new Date().getTime() / 1000);
      const start = Math.floor(new Date(startTime).getTime() / 1000);
      const readyTime = start + 120;
      const diff = readyTime - now;

      if (now < readyTime) {
        setShowWaiting(true);
        const minutes = Math.floor(diff / 60);
        const seconds = diff % 60;
        setWaitingTime(
          `${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`
        );
      } else {
        setShowWaiting(false);
      }
    };

    startCountdown();

    // Update countdown every second
    const intervalId = setInterval(startCountdown, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [startTime]);

  useEffect(() => {
    const checkEndTime = () => {
      const now = Math.floor(new Date().getTime() / 1000);
      const end = Math.floor(new Date(endTime).getTime() / 1000);

      if (now >= end) {
        history.push('/replaystreaming');
      }
    };

    checkEndTime();
    const intervalId = setInterval(checkEndTime, 1000);

    return () => clearInterval(intervalId);
  }, [endTime]);

  const _backToHomeButtonOnClick = () => {
    backToHomeButtonOnClick();
  };

  const _rejoinButtonOnClick = () => {
    setIsLobby(false);
    setConferenceLeft(false);
    // setIsLoading(true);
  };

  const _nameOnChange = (e: { target: { value: string } }) => {
    setName({ value: e.target.value, error: false });
  };

  const _emailOnChange = (e: { target: { value: string } }) => {
    const error = validateEmail(e.target.value);
    setEmail({ value: e.target.value, error });
  };

  // const _validate = () => {
  //   let isValidate = true;
  //   if (email.error) {
  //     isValidate = false;
  //     setEmail({ ...email, error: true });
  //   }
  //   return isValidate;
  // };

  const _getJwtTokenButtonOnClick = () => {
    // if (_validate()) {
    //   const data = {
    //     roomId: '',
    //     roomName,
    //     userName: name?.value,
    //     userEmail: email?.value,
    //   };
    // }
  };

  const _renderConferenceLeftView = () => (
    <>
      <Card customStyles={classes.bannerCard}>
        <Clock id="lobby-clock" />
      </Card>
      <Card customStyles={classes.useInfoCard}>
        <Typography variant="h5" className={classes.cardHeading}>
          Thank you for joining with us!
        </Typography>
        {(email?.value || name?.value) && (
          <>
            <Typography variant="body2" color="textSecondary">
              You logged in as a
            </Typography>
            <Grid container className={classes.profileDetailsWrapper}>
              <Grid xs={3}>
                <Avatar alt="Remy Sharp" />
              </Grid>
              <Grid>
                <Typography variant="body2">{name?.value}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {email?.value}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
        <div className={classes.buttonWrapper}>
          <Button
            id="rejoin-button-in-disconnect"
            variant="outlined"
            color="primary"
            label={intl.formatMessage({
              id: 'rejoin',
            })}
            customStyles={classes.rejoin}
            onClick={_rejoinButtonOnClick}
          />
          <Button
            id="back-to-home-in-disconnect"
            variant="contained"
            color="primary"
            label={intl.formatMessage({
              id: 'back_to_home',
            })}
            onClick={_backToHomeButtonOnClick}
          />
        </div>
      </Card>
    </>
  );
  const _renderCollectUserInfoView = () => (
    <>
      <Card customStyles={classes.bannerCard}>
        <Clock id="lobby-clock" />
      </Card>
      <Card customStyles={classes.useInfoCard}>
        <Typography
          component="h1"
          variant="h5"
          className={classes.textOverflow}
        >
          {roomTitle}
        </Typography>
        <TextField
          label={intl.formatMessage({
            id: 'name',
          })}
          value={name.value}
          onChange={_nameOnChange}
        />
        <TextField
          label={intl.formatMessage({
            id: 'email',
          })}
          value={email.value}
          onChange={_emailOnChange}
          error={email.error}
          helperText={
            email.error &&
            intl.formatMessage({
              id: 'please_insert_valid_email',
            })
          }
        />
        <Button
          id="get-jwt-token-button"
          variant="contained"
          label={intl.formatMessage({
            id: 'join',
          })}
          onClick={_getJwtTokenButtonOnClick}
        />
      </Card>
    </>
  );

  const _renderLobby = () => (
    <Grid container className={classes.root}>
      <Grid item xs={12} md={4} className={classes.leftGrid}>
        {/* <Card>ssdsd</Card> */}
        {conferenceLeft
          ? _renderConferenceLeftView()
          : _renderCollectUserInfoView()}
      </Grid>
    </Grid>
  );

  const _renderConferene = () => (
    <>
      {/* {isLoading && <LoadingLayout />} */}
      {showWaiting && <WaitingTime time={waitingTime} />}
      {viewingUrl && !showWaiting && (
        <div className={classes.conferenceContainer}>
          <ReactPlayer
            url={viewingUrl}
            width="100%"
            height="100%"
            playing={playing}
            onEnded={() => {
              // Toggle playing state to restart the video
              setPlaying(false);
              setTimeout(() => setPlaying(true), 10);
            }}
            config={{
              youtube: {
                playerVars: {
                  showinfo: 1,
                  autoplay: 1,
                  controls: 1,
                  loop: 1,
                  fs: 1,
                  rel: 0,
                },
              },
            }}
          />
        </div>
      )}
    </>
  );

  return <>{isLobby ? _renderLobby() : _renderConferene()}</>;
};

export default memo(withStyles(styles)(ReplaystreamingRoomView));
